// Is Your Type Actually Your Type? — Attraction Alignment scoring data
// Linear 10-question weighted archetype tool. Internal name: attraction-alignment.

export const ARCHETYPES = {
  balanced_selector: {
    label: 'Balanced Selector',
    narrative:
      "What attracts you and what actually fits seem reasonably aligned. That doesn't mean you ignore chemistry. It means it probably isn't making every decision for you.",
    bullets: ['Attraction + compatibility balance', 'Healthy discernment', 'Openness beyond looks'],
  },
  chemistry_driven: {
    label: 'Chemistry-Driven',
    narrative:
      "Immediate spark appears to carry a lot of weight in your decision-making. Chemistry matters. But when it becomes the primary filter, compatibility can get overlooked.",
    bullets: ['Spark-led selection', 'Chemistry as filter', 'Compatibility may take a back seat'],
  },
  familiar_chaos: {
    label: 'Familiar Chaos',
    narrative:
      "You may be drawn toward emotional dynamics that feel familiar, even when they haven't consistently worked well. Familiar doesn't always mean compatible.",
    bullets: ['Repeating patterns', 'Familiarity bias', 'Intensity often confused for fit'],
  },
  looks_first_filter: {
    label: 'Looks-First Filter',
    narrative:
      "Physical attraction may be doing more decision-making than you realize. That's human. The question is whether it's helping you find what you actually want.",
    bullets: ['Visual-first attention', 'Appearance weighted heavily', 'Worth examining the trade-off'],
  },
  novelty_seeker: {
    label: 'Novelty Seeker',
    narrative:
      "Excitement, unpredictability, or emotional intensity may be disproportionately attractive. The spark is real. Whether it aligns with your actual goals is worth examining.",
    bullets: ['Novelty bias', 'Intensity as signal', 'Stability may read as boring'],
  },
  mixed_signals: {
    label: 'Mixed Signals',
    narrative:
      "What you say you want and what seems to pull your attention may not be fully aligned yet. That's useful information.",
    bullets: ['Internal contradiction', 'Stated vs. actual diverge', 'Worth a closer look'],
  },
};

// Stated-desired connection options from Q9 (used for results context only)
export const STATED_INTENT_OPTIONS = [
  'Long-term partnership',
  'Meaningful dating',
  'Casual connection',
  'Exploration',
  'Companionship',
  'Flexible / open connection',
];

export const QUESTIONS = [
  {
    id: 'a1',
    type: 'choice',
    prompt: 'When someone catches your attention, what usually happens first?',
    options: [
      { label: 'Their look grabs me immediately', scores: { looks_first_filter: 5 } },
      { label: 'Their energy pulls me in', scores: { chemistry_driven: 4, novelty_seeker: 2 } },
      { label: 'Great conversation', scores: { balanced_selector: 3 } },
      { label: 'Emotional intrigue', scores: { familiar_chaos: 4, chemistry_driven: 2 } },
      { label: 'Shared values / compatibility', scores: { balanced_selector: 5 } },
      { label: 'It depends', scores: { mixed_signals: 3 } },
    ],
  },
  {
    id: 'a2',
    type: 'choice',
    prompt: 'Be honest: what feels most exciting?',
    options: [
      { label: 'Instant chemistry', scores: { chemistry_driven: 5 } },
      { label: 'Mystery', scores: { novelty_seeker: 4, familiar_chaos: 1 } },
      { label: 'Confidence', scores: { chemistry_driven: 2, balanced_selector: 1 } },
      { label: 'Emotional intensity', scores: { familiar_chaos: 4 } },
      { label: 'Stability that still feels attractive', scores: { balanced_selector: 4 } },
      { label: 'Something unexpected', scores: { novelty_seeker: 4 } },
    ],
  },
  {
    id: 'a3',
    type: 'choice',
    prompt: 'Which sounds most familiar?',
    options: [
      { label: "I've ignored red flags because the chemistry was strong", scores: { chemistry_driven: 4, familiar_chaos: 2 } },
      { label: "I've confused attraction with compatibility", scores: { looks_first_filter: 3, chemistry_driven: 2 } },
      { label: "I've chosen excitement over stability", scores: { novelty_seeker: 4 } },
      { label: 'I tend to know what works for me', scores: { balanced_selector: 5 } },
      { label: "I honestly don't know", scores: { mixed_signals: 4 } },
    ],
  },
  {
    id: 'a4',
    type: 'slider',
    prompt: 'How much does physical attraction influence your first impression?',
    min: 1,
    max: 5,
    labels: ['Low', 'Very high'],
    scoring: [
      { value: 1, scores: { balanced_selector: 3 } },
      { value: 2, scores: { balanced_selector: 2 } },
      { value: 3, scores: { chemistry_driven: 1 } },
      { value: 4, scores: { looks_first_filter: 3 } },
      { value: 5, scores: { looks_first_filter: 5 } },
    ],
  },
  {
    id: 'a5',
    type: 'choice',
    prompt: 'What usually makes someone feel unforgettable?',
    options: [
      { label: 'Their appearance', scores: { looks_first_filter: 4 } },
      { label: 'Their emotional intensity', scores: { familiar_chaos: 4 } },
      { label: 'Their confidence', scores: { chemistry_driven: 3 } },
      { label: 'Their unpredictability', scores: { novelty_seeker: 4 } },
      { label: 'How I feel around them', scores: { balanced_selector: 2, chemistry_driven: 1 } },
      { label: 'Consistency and depth', scores: { balanced_selector: 5 } },
    ],
  },
  {
    id: 'a6',
    type: 'choice',
    prompt: "Have you ever been drawn to someone you knew probably wasn't a great fit?",
    options: [
      { label: 'More than once', scores: { familiar_chaos: 3, chemistry_driven: 1 } },
      { label: 'Definitely', scores: { familiar_chaos: 3, mixed_signals: 1 } },
      { label: 'Occasionally', scores: { chemistry_driven: 2, mixed_signals: 1 } },
      { label: 'Rarely', scores: { balanced_selector: 3 } },
      { label: 'Not really', scores: { balanced_selector: 4 } },
      { label: 'Honestly, yes, repeatedly', scores: { familiar_chaos: 5 } },
    ],
  },
  {
    id: 'a7',
    type: 'choice',
    prompt: 'What sounds most true?',
    options: [
      { label: 'Calm can feel boring', scores: { novelty_seeker: 4 } },
      { label: 'Predictability kills attraction', scores: { novelty_seeker: 4, chemistry_driven: 1 } },
      { label: 'I need strong chemistry', scores: { chemistry_driven: 5 } },
      { label: 'I value stability if attraction is there', scores: { balanced_selector: 4 } },
      { label: "I've probably repeated some patterns", scores: { familiar_chaos: 4 } },
    ],
  },
  {
    id: 'a8',
    type: 'choice',
    prompt: "If someone checks every practical box but doesn't create instant spark, what happens?",
    options: [
      { label: 'I usually lose interest', scores: { chemistry_driven: 4 } },
      { label: "I'd give it a chance", scores: { balanced_selector: 4 } },
      { label: 'Depends', scores: { mixed_signals: 3 } },
      { label: 'I probably overthink it', scores: { mixed_signals: 2, familiar_chaos: 1 } },
      { label: 'Instant spark matters a lot', scores: { chemistry_driven: 5 } },
    ],
  },
  {
    id: 'a9',
    type: 'choice',
    prompt: 'What do you say you want most?',
    storedAs: 'stated_desired_connection_type',
    options: [
      { label: 'Long-term partnership', scores: { balanced_selector: 2 } },
      { label: 'Meaningful dating', scores: { balanced_selector: 1 } },
      { label: 'Casual connection', scores: {} },
      { label: 'Exploration', scores: { novelty_seeker: 1 } },
      { label: 'Companionship', scores: { balanced_selector: 1 } },
      { label: 'Flexible / open connection', scores: {} },
    ],
  },
  {
    id: 'a10',
    type: 'choice',
    prompt: "Gut check: what's probably influenced your dating choices the most?",
    options: [
      { label: 'Attraction', scores: { looks_first_filter: 3, chemistry_driven: 1 } },
      { label: 'Chemistry', scores: { chemistry_driven: 4 } },
      { label: 'Familiarity', scores: { familiar_chaos: 5 } },
      { label: 'Hope', scores: { mixed_signals: 3 } },
      { label: 'Excitement', scores: { novelty_seeker: 4 } },
      { label: 'Actual compatibility', scores: { balanced_selector: 5 } },
    ],
  },
];

export const calculateResult = (answers) => {
  const totals = {};
  Object.keys(ARCHETYPES).forEach((k) => (totals[k] = 0));

  answers.forEach((ans, i) => {
    const q = QUESTIONS[i];
    if (!q || ans === null || ans === undefined) return;
    if (q.type === 'choice') {
      const scores = q.options[ans]?.scores;
      if (scores) {
        Object.entries(scores).forEach(([k, v]) => {
          totals[k] = (totals[k] || 0) + v;
        });
      }
    } else if (q.type === 'slider') {
      const match = q.scoring.find((s) => s.value === ans);
      if (match?.scores) {
        Object.entries(match.scores).forEach(([k, v]) => {
          totals[k] = (totals[k] || 0) + v;
        });
      }
    }
  });

  const winner = Object.entries(totals).reduce((a, b) => (b[1] > a[1] ? b : a));
  return { archetype: winner[0], scores: totals, result: ARCHETYPES[winner[0]] };
};

// Pull the stated desired connection type from Q9 answer index
export const getStatedIntent = (answers) => {
  const q9 = QUESTIONS.find((q) => q.id === 'a9');
  const idx = answers[QUESTIONS.indexOf(q9)];
  if (idx === null || idx === undefined) return null;
  return q9.options[idx]?.label || null;
};
